import {createPopper, Instance} from '@popperjs/core';
import {Placement} from '@popperjs/core/lib/enums';
import {PositioningStrategy} from '@popperjs/core/lib/types';
import React, {MutableRefObject, ReactNode, RefObject, useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {useOutsideClick} from '~/lib/outsideClick';
import style from './index.module.scss';

export interface IPopperOptions {
    placement?: Placement;
    strategy?: PositioningStrategy;
}

export const defaultPopperOptions: IPopperOptions = {
    placement: 'bottom'
};

export interface IPopperProps extends IPopperOptions {
    children: ReactNode;
    parentRef: RefObject<HTMLElement | null>;
    onOutsideClick?: () => void;
}

export interface IWithPopper {
    usePopper?: boolean;
    popperOptions?: IPopperOptions;
    dropUp?: boolean;
}

export const Popper = ({
    children,
    placement = 'auto',
    strategy,
    parentRef,
    onOutsideClick
}: IPopperProps) => {
    const [popperInstance, setPopperInstance] = useState<Instance>(null);
    const popperRef = useRef();
    const arrowRef = useRef();

    useOutsideClick(popperRef, onOutsideClick);

    useEffect(() => {
        if (!popperInstance) {
            const popper = createPopper(parentRef.current, popperRef.current, {
                placement,
                strategy,
                modifiers: [{
                    name: 'arrow',
                    options: {
                        element: arrowRef.current,
                        mainAxis: false
                    }
                }]
            });
            popper.forceUpdate();
            setPopperInstance(popper);
        } else if (popperInstance) {
            popperInstance.destroy();
            setPopperInstance(null);
        }
    }, [popperRef, parentRef]);

    useEffect(() => {
        if (popperInstance) {
            popperInstance.forceUpdate();
        }
    }, [children]);

    return ReactDOM.createPortal(<div ref={popperRef} className={style.popper}>
        <div ref={arrowRef} className={style.popperArrow}/>
        {children}
    </div>, document.body);
};
